/**
 * @fileoverview ModuleRenderer dispatch (header / contacts / violations).
 * @module inspection-renderer/modules
 *
 * Responsibilities:
 * - Inspect the form's `formKind` + `moduleConfig` and dispatch to:
 *   - `header`  → standard form rendered by `<SectionRenderer>` (host wires).
 *   - `module` + `moduleType:'contacts'`   → `<ContactsModule>`.
 *   - `module` + `moduleType:'violations'` → `<ViolationsModule>`.
 * - For unknown module types, render a small `role="alert"` panel so drift
 *   is detectable in tests.
 *
 * Dependencies:
 * - React 18 (peer).
 * - The two concrete module components.
 * - `@firesquirrel/core-domain` form types.
 *
 * Architecture:
 * - Presentation-only. Pure dispatch.
 *
 * @component ModuleRenderer
 *
 * @contract ModuleRendererProps
 * Purpose: prop contract for the module dispatcher.
 */
import { type ReactNode } from 'react';
import type { InspectionForm } from '@firesquirrel/core-domain';
import { ViolationsModule } from './ViolationsModule';
import type { ContactEntry } from '@firesquirrel/core-domain';
import { ContactsModule } from './ContactsModule';
import type { LoggedViolation, ContactChange } from '../violations/types';

export interface ModuleRendererProps {
  form: InspectionForm;
  /** Existing violations (filter by source='additional' when displaying). */
  violations: LoggedViolation[];
  /** Existing contacts (for the contacts module). */
  contacts: ContactEntry[];
  readOnly?: boolean;
  onContactsChange?: (nextContacts: ContactEntry[], changes: ContactChange[]) => void;
  onViolationsChange?: (nextViolations: LoggedViolation[]) => void;
  /** When `formKind === 'standard' | 'header'`, the dispatcher returns null
   *  so the SectionRenderer can take over upstream. */
}

export function ModuleRenderer(props: ModuleRendererProps): ReactNode {
  const { form, violations, contacts, readOnly,
    onContactsChange, onViolationsChange } = props;

  // Only dispatch true modules; header/standard fall through to the
  // SectionRenderer pipeline.
  if (form.formKind !== 'module' || !form.moduleConfig) {
    return null;
  }

  const cfg = form.moduleConfig;
  switch (cfg.moduleType) {
    case 'contacts':
      return (
        <ContactsModule
          config={cfg}
          contacts={contacts}
          readOnly={readOnly}
          onChange={onContactsChange ?? (() => undefined)}
        />
      );
    case 'violations':
      return (
        <ViolationsModule
          config={cfg}
          violations={violations.filter((v) => v.source === 'additional')}
          readOnly={readOnly}
          onChange={onViolationsChange ?? (() => undefined)}
        />
      );
    default:
      return (
        <div role="alert" data-module-error="unknown" className="border border-red-200 bg-red-50 rounded-sm p-2 text-xs text-red-700">
          Unknown module type
        </div>
      );
  }
}
